import { Controller } from "@hotwired/stimulus"

// One online payment's page (Events v2 Phase 3). Asks the server for the payment's PaymentIntent (a POST, so opening
// the page makes nothing at Stripe), mounts Stripe's Payment Element on the restaurant's connected account in the
// page's theme, and confirms in place. A card that needs a bank's check (3D Secure) leaves for it and comes back to the
// page's own address, which reads Stripe's word on the row: the address Stripe adds is tidied away, never trusted.
// When the server answers anything but open (paid in another tab, cancelled, changed), the form closes with why.
export default class extends Controller {
  static targets = ["form", "element", "error", "button", "loading", "closed"]
  static values = {
    key: String, account: String, intentUrl: String, returnUrl: String, appearance: Object, fontUrl: String,
    failedMessage: String
  }

  connect() {
    this.tidyAddress()
    if (this.hasFormTarget) this.start()
  }

  disconnect() {
    this.paymentElement?.destroy()
  }

  tidyAddress() {
    const url = new URL(window.location.href)
    const added = ["payment_intent", "payment_intent_client_secret", "redirect_status"]
    if (!added.some((name) => url.searchParams.has(name))) return

    added.forEach((name) => url.searchParams.delete(name))
    window.history.replaceState(window.history.state, "", url)
  }

  async start() {
    this.loadingTarget.hidden = false
    const answer = await this.fetchIntent()
    if (answer.state !== "open") return this.close(answer.message || this.failedMessageValue)

    this.clientSecret = answer.client_secret
    this.mount()
  }

  // The server's word on the payment: { state, client_secret } while it's open, else { state, message }.
  async fetchIntent() {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content || ""
      const response = await fetch(this.intentUrlValue, {
        method: "POST", credentials: "same-origin", headers: { "X-CSRF-Token": token, Accept: "application/json" }
      })
      return await response.json()
    } catch (error) {
      return { state: "unavailable", message: this.failedMessageValue }
    }
  }

  mount() {
    if (!window.Stripe) return this.close(this.failedMessageValue)

    this.stripe = window.Stripe(this.keyValue, { stripeAccount: this.accountValue })
    this.elements = this.stripe.elements({
      clientSecret: this.clientSecret,
      appearance: this.appearanceValue,
      fonts: this.fontUrlValue ? [{ cssSrc: this.fontUrlValue }] : []
    })
    // The page's own consent line above Pay says what the saved card may be charged for; Stripe's would repeat it.
    // Link stays off: it offers a bank payment and Klarna inside the card option, which a card-only intent that
    // saves the card for a later charge can't take.
    this.paymentElement = this.elements.create("payment", {
      wallets: { applePay: "auto", googlePay: "auto", link: "never" },
      terms: { card: "never" }
    })
    this.paymentElement.on("ready", () => {
      this.loadingTarget.hidden = true
      this.buttonTarget.disabled = false
    })
    // Stripe couldn't draw the form (the account can't take cards, say): never leave the guest on "Loading".
    this.paymentElement.on("loaderror", () => this.close(this.failedMessageValue))
    // The guest is fixing the card or picking another method: the last attempt's message no longer applies.
    this.paymentElement.on("change", () => this.showError(null))
    this.paymentElement.mount(this.elementTarget)
  }

  async pay(event) {
    event.preventDefault()
    if (this.paying || !this.elements) return

    this.paying = true
    this.buttonTarget.disabled = true
    this.showError(null)
    let error
    try {
      ({ error } = await this.stripe.confirmPayment({
        elements: this.elements, confirmParams: { return_url: this.returnUrlValue }, redirect: "if_required"
      }))
    } catch (thrown) {
      return this.retry(this.failedMessageValue)
    }
    if (!error) return window.location.replace(this.returnUrlValue)

    // A declined or mistyped card: Stripe's words, and the guest tries again in the same form.
    if (error.type === "card_error" || error.type === "validation_error") return this.retry(error.message)

    // Anything else may mean the payment moved under the form (paid in another tab, cancelled by staff): ask again.
    const answer = await this.fetchIntent()
    if (answer.state !== "open") return this.close(answer.message || this.failedMessageValue)
    if (answer.client_secret !== this.clientSecret) return window.location.reload()

    this.retry(error.message || this.failedMessageValue)
  }

  retry(message) {
    this.showError(message)
    this.paying = false
    this.buttonTarget.disabled = false
  }

  showError(message) {
    this.errorTarget.textContent = message || ""
    this.errorTarget.hidden = !message
  }

  // Once only: a late loaderror, or a second answer, finds the form gone. The element goes with it, so none of its
  // handlers fire after.
  close(message) {
    if (!this.hasFormTarget) return

    this.paymentElement?.destroy()
    this.paymentElement = null
    this.formTarget.remove()
    this.closedTarget.textContent = message
    this.closedTarget.hidden = false
  }
}
